import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Kbd,
  Popover,
  Skeleton,
  Stack,
  Text,
  useMatches
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { Spotlight, spotlight } from "@mantine/spotlight";
import { IconFilter, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import type { TQuicksearchWeightedSite } from "./Quicksearch.types";
import { QuicksearchGenerator } from "./Results/QuicksearchGenerator";
import { QuicksearchSite } from "./Results/QuicksearchSite";
import { QuicksearchUnternehmen } from "./Results/QuicksearchUnternehmen";
import { QuicksearchVermarktungsvertrag } from "./Results/QuicksearchVermarktungsvertrag";
import { useQuicksearch } from "./useQuicksearch";
import { filterAndSortGroups } from "./utils/quicksearch";

function Quicksearch() {
  const [query, setQuery] = useState("");
  const { data, isLoading } = useQuicksearch();
  const [searchFavorites, setSearchFavorites] = useLocalStorage<
    Array<TQuicksearchWeightedSite>
  >({
    key: "site-quick-switch-favorites",
    defaultValue: [],
    getInitialValueInEffect: false
  });

  const searchGroups = [
    {
      label: "Liegenschaften",
      items: query && data?.sites ? data.sites : searchFavorites || [],
      Component: ({ item }) => (
        <QuicksearchSite
          highlight={query.split(" ")}
          item={item}
          setSearchFavorites={setSearchFavorites}
        />
      )
    },
    {
      label: "Vermarktungsverträge",
      items:
        query && data?.direktvermarktungsvertraege
          ? data.direktvermarktungsvertraege
          : [],
      Component: ({ item }) => (
        <QuicksearchVermarktungsvertrag
          highlight={query.split(" ")}
          item={item}
        />
      )
    },
    {
      label: "Unternehmen",
      items: query && data?.persons ? data.persons : [],
      Component: ({ item }) => (
        <QuicksearchUnternehmen highlight={query.split(" ")} item={item} />
      )
    },
    {
      label: "Erzeuger",
      items: query && data?.generators ? data.generators : [],
      Component: ({ item }) => (
        <QuicksearchGenerator highlight={query.split(" ")} item={item} />
      )
    }
  ];

  const [selectedGroups, setSelectedGroups] = useState<string[]>(
    searchGroups.map((group) => group.label)
  );

  // Filter and limit results across all groups
  const lowerQuery = query.toLowerCase();
  const buttonSize = useMatches({ base: "sm", md: "lg" });

  const filteredGroups = filterAndSortGroups(
    searchGroups,
    selectedGroups,
    lowerQuery
  );

  return (
    <>
      <Button
        size={buttonSize}
        title="Liegenschaft finden (Strg + K)"
        variant="default"
        onClick={spotlight.open}
      >
        <Group gap="xs" visibleFrom="md">
          <Text c="dimmed" fz="sm">
            Suchen
          </Text>
          <Kbd fz="xs">Ctrl + K</Kbd>
        </Group>
        <Box hiddenFrom="md">
          <IconSearch />
        </Box>
      </Button>

      <Spotlight.Root query={query} onQueryChange={setQuery}>
        <Group>
          <Spotlight.Search
            flex={1}
            leftSection={<IconSearch stroke={1.5} />}
            placeholder="Suche nach Liegenschaften und mehr..."
          />
          <QuickSearchFilter
            groups={searchGroups.map((group) => group.label)}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
          />
        </Group>
        <Spotlight.ActionsList>
          {filteredGroups.map((group) => (
            <Spotlight.ActionsGroup key={group.label} label={group.label}>
              {group.items.map((item) => {
                const Component = group.Component;
                return <Component item={item} key={item.id} />;
              })}
            </Spotlight.ActionsGroup>
          ))}
          {filteredGroups.length === 0 && !isLoading && (
            <Spotlight.Empty>Keine Ergebnisse gefunden...</Spotlight.Empty>
          )}
          {query && isLoading && <LoadingSkeleton />}
        </Spotlight.ActionsList>
      </Spotlight.Root>
    </>
  );
}

function LoadingSkeleton() {
  return Array.from({ length: 5 }).map((_, index) => (
    <Skeleton height={20} key={index} mb={10} />
  ));
}

function QuickSearchFilter({
  selectedGroups,
  setSelectedGroups,
  groups
}: {
  selectedGroups: string[];
  setSelectedGroups: (groups: string[]) => void;
  groups: string[];
}) {
  return (
    <Popover position="bottom" shadow="md" width={200} withArrow>
      <Popover.Target>
        <ActionIcon>
          <IconFilter stroke={1.5} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown style={{ zIndex: 10000 }}>
        <Stack gap="xs">
          {groups.map((group) => {
            const isSelected = selectedGroups.includes(group);
            return (
              <Checkbox
                checked={isSelected}
                key={group}
                label={group}
                onChange={() => {
                  if (isSelected) {
                    setSelectedGroups(
                      selectedGroups.filter((g) => g !== group)
                    );
                  } else {
                    setSelectedGroups([...selectedGroups, group]);
                  }
                }}
              />
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export { Quicksearch };
